/**
 * @file ModelIcon.jsx
 * @description A component that dynamically displays an SVG icon for a given AI model name.
 * 
 * @component ModelIcon
 * @props {string} modelName - The name of the model (e.g., "Route AI", "Claude 3 Sonnet").
 * @props {number} [size=16] - The desired width and height for the icon.
 * 
 * @state {string | null} iconSrc - Stores the result of the icon import. Can be:
 *   - `null`: The initial state while loading.
 *   - `'fallback'`: If the icon could not be found.
 *   - A string path to the SVG: If the icon was successfully loaded.
 * 
 * @imports
 * - React (useState, useEffect): For managing the icon source state and performing the side-effect of loading.
 * - Lucide-react (Bot): Used as the default/fallback icon.
 * 
 * @purpose This component provides a flexible way to associate model names with specific brand icons without needing to import all icons upfront. It uses a dynamic `import()` statement to asynchronously load only the required SVG from the `/assets` folder. If an icon is not found, it gracefully falls back to a generic `Bot` icon. It also includes an in-memory cache to prevent re-loading the same icon multiple times, improving performance.
 */

import React, { useState, useEffect } from 'react';
import { Bot } from 'lucide-react';

// --- PERFORMANCE OPTIMIZATION: In-Memory Cache ---
// We use a Map to cache the results of our dynamic imports.
// This prevents the component from re-fetching the same icon asset every time it renders.
// The Map is declared outside the component so it persists across all instances and re-renders.
const iconCache = new Map();

/**
 * A pure helper function to convert a model name into a file-friendly format.
 * Example: "Claude 3 Sonnet (Google)" -> "claude-3-sonnet-google"
 * @param {string} name - The raw model name.
 * @returns {string} The formatted name for use in the import path.
 */
// IMPORTANT: The second replace intentionally strips dots and parentheses so that
// version numbers and suffixes map to their SVG filenames correctly:
//   "Claude Opus 4.5"      → "claude-opus-45.svg"      (dot stripped, not kept)
//   "Kimi K2 (Reasoning)"  → "kimi-k2-reasoning.svg"   (parens stripped)
//   "GPT-5.1"              → "gpt-51.svg"
// SVG assets MUST be named with this same convention. Do not change this regex
// without renaming all affected asset files.
const formatModelNameForIcon = (name) => {
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[().]/g, '');
};

const ModelIcon = ({ modelName, size = 16 }) => {
    // State to hold the source path of the icon, or a status string.
    const [iconSrc, setIconSrc] = useState(null);

    // This effect is responsible for loading the icon when the modelName changes.
    useEffect(() => {
        // Create a file-friendly name from the model name prop.
        const iconName = formatModelNameForIcon(modelName);

        // --- Step 1: Check the cache first ---
        if (iconCache.has(iconName)) {
            // If the icon is already in our cache, use it directly and skip the network request.
            setIconSrc(iconCache.get(iconName));
            return; // End the effect early.
        }

        // --- Step 2: If not cached, try to load the icon ---
        const loadIcon = async () => {
            try {
                // This is a dynamic import. Webpack sees this and automatically code-splits
                // the asset. It only gets bundled and loaded by the browser when this line runs.
                const { default: src } = await import(`../../assets/${iconName}.svg`);
                
                // On success, update the state with the SVG's path.
                setIconSrc(src);
                // And, most importantly, add the successful result to our cache for next time.
                iconCache.set(iconName, src);

            } catch (error) {
                // If the `import()` fails (e.g., the file doesn't exist), this block runs.
                console.warn(`Icon for ${modelName} (${iconName}.svg) not found. Using fallback.`);
                
                // We update the state to 'fallback' to signal that we should render the default icon.
                setIconSrc('fallback');
                // We also cache the 'fallback' result so we don't try to fetch this missing icon again.
                iconCache.set(iconName, 'fallback');
            }
        };

        loadIcon();
    }, [modelName]); // The effect re-runs only when the `modelName` prop changes.


    // --- Render Logic ---
    // This logic is now cleaner and more explicit.

    // Case 1: The import failed. Render the fallback icon.
    if (iconSrc === 'fallback') {
        return <Bot size={size} />;
    }

    // Case 2: The import succeeded. Render the custom SVG icon as an <img>.
    if (iconSrc) {
        return <img src={iconSrc} alt={`${modelName} logo`} width={size} height={size} />;
    }

    // Case 3 (Default): The import is still in progress (iconSrc is null).
    // We render the fallback icon immediately so there's no layout shift or empty space.
    // When the real icon loads, it will seamlessly replace this one.
    return <Bot size={size} />;
};

export default ModelIcon;